/*
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

/**
 * Identifies one backend request, for both issuing it and reporting a failure of it.
 *
 * A `fetch()` rejection carries nothing about what was being fetched - every browser throws a
 * bare `TypeError` whose message ("Failed to fetch", "Load failed", "NetworkError when
 * attempting to fetch resource") names neither the URL nor the method, and no stack survives
 * minification for the async frames. Sentry therefore groups failures of unrelated endpoints
 * into one issue, and the reader cannot tell which request produced any given event (#368).
 * Deriving both the request and its error tags from one value is what stops the tag from
 * disagreeing with what was actually sent.
 */
export interface FetchRequestDescriptor {
  readonly endpoint: string
  readonly method: "GET" | "POST"
}

/**
 * The POST that runs the calculation. Failures surface via `fetchInitialScrs` and are reported
 * by `UserParametersForm`.
 */
export const INITIAL_SCRS_REQUEST: FetchRequestDescriptor = {
  endpoint: "/api/v2/compute-initial-scrs",
  method: "POST",
}

/**
 * The GET behind the data-coverage banner. Failures surface via, and are reported by,
 * `useDataInfo`.
 */
export const DATA_INFO_REQUEST: FetchRequestDescriptor = {
  endpoint: "/api/v2/data-info",
  method: "GET",
}

/**
 * Tags identifying which request a reported failure came from.
 *
 * Sentry tags are the indexed, searchable dimension - `extra` is neither, so an endpoint
 * recorded there cannot be searched, filtered or grouped on, which is what triage actually
 * needs. This is the single definition of those two tag names: two call sites spelling the
 * same dimension differently ("endpoint" vs "url") would split every dashboard built on it.
 */
// AIDEV-NOTE: this deliberately does not capture the errors itself. Both call sites already
// call Sentry.captureException in their own catch, and capturing here as well would bill and
// group each network failure twice against a 5,000 event/month quota. Reporting stays at the
// boundary that already owns it; this only supplies the context it was missing.
export const fetchErrorTags = (
  request: FetchRequestDescriptor
): { readonly endpoint: string; readonly method: string } => ({
  endpoint: request.endpoint,
  method: request.method,
})

/**
 * Whether an error is a browser network-layer `fetch()` rejection.
 *
 * Every engine rejects a failed `fetch()` with a `TypeError`, while the message ("Failed to
 * fetch" / "Load failed" / "NetworkError when attempting to fetch resource") is per-engine, so
 * a message test goes dead the day an engine rewords it. Read off `name` rather than via
 * `instanceof TypeError` because `name` is the exact field Sentry reports as the exception
 * type, so this classifier and Sentry's own view of the event cannot disagree.
 */
// AIDEV-NOTE: (#991) One definition, two readers with different jobs: `fetchErrorFingerprint`
// below decides Sentry grouping, `classifyFetchFailure` decides what the user is told. They
// answered the same question separately before this, and a second spelling of "is this a
// network fault" is exactly the drift `fetchErrorTags` above exists to prevent for tag names.
//
// AIDEV-NOTE: (#991 review) That second reader widens what this heuristic decides: a `TypeError`
// thrown by a *defect* in the response-handling path, rather than by the network, now tells the
// user their connection may have dropped and files the event as `connectivity_error` instead of
// `calculation_error`. Accepted rather than narrowed, for the reason the note below gives: the
// only sharper signal available is the message, which is per-engine and goes dead the day one
// rewords it. `initialScrs.ts` already retries any `TypeError` before headers arrive on the same
// reasoning ("an accepted, deliberate choice"), so this extends an existing bet rather than
// taking a new one. The mitigation is that the misdirected event still carries `endpoint`,
// `method` and its own stack, so triage is slower rather than blind.
const isNetworkLayerError = (error: unknown): boolean =>
  error instanceof Error && error.name === "TypeError"

/**
 * First element of a fetch-failure fingerprint, marking the group as a grouping decision this
 * codebase made rather than one of Sentry's computed hashes.
 */
const FETCH_FAILURE_FINGERPRINT_PREFIX = "fetch-failure"

/**
 * Sentry grouping key for a browser network-layer fetch failure, or null for any other error.
 *
 * Sentry groups a JavaScript error on its stack frames when it has one, and falls back to
 * exception type plus value when it has none. A `fetch()` rejection supplies neither reliably:
 * the async frames frequently do not survive minification, and the value is whichever wording
 * the engine chose for a single condition. So one endpoint failing the same way twice can land
 * in two issues - which is how JAVASCRIPT-REACT-31 came to exist beside a still-resolved
 * JAVASCRIPT-REACT-F rather than regressing it (#973), leaving "resolve it and watch for a
 * regression" quietly inert and the occurrence counts split across groups.
 *
 * Endpoint and method are therefore the whole key. The exception value is deliberately excluded:
 * it is the field that varies by browser, so including it would rebuild the split.
 */
// AIDEV-NOTE (#973): this regroups a network-layer fetch failure only, never every error that
// reaches a fetch call site, and that restraint is the substance of the fix. The same catch
// blocks also see a header-phase timeout whose message is the value half of
// JAVASCRIPT-REACT-2A's grouping key - the history #843 is waiting on a recurrence of - plus
// non-ok-status and response-validation failures, which are distinct faults earning distinct
// issues. Fingerprinting all of them alike would merge the lot and detach that history: the same
// silent mis-grouping this fixes, running the other way.
// AIDEV-NOTE: classified on the exception type rather than the message, for the reason the #476
// note in initialScrs.ts sets out - see `isNetworkLayerError` above, which is that test.
// The array is mutable because Sentry's `ScopeContext.fingerprint` is typed `string[]`; it is
// built fresh per call, so there is nothing shared for a caller to mutate.
export const fetchErrorFingerprint = (
  error: unknown,
  request: FetchRequestDescriptor
): string[] | null =>
  isNetworkLayerError(error)
    ? [FETCH_FAILURE_FINGERPRINT_PREFIX, request.endpoint, request.method]
    : null

/**
 * Which part of a request an abort interrupted.
 *
 * - `"headers"` - the deadline fired before any response headers arrived, so nothing is known
 *   about whether the backend started, finished, or was even reached.
 * - `"body"` - headers had arrived and a body deadline fired - the bytes stopped, or were still
 *   arriving too slowly when the total cap ran out ({@link BodyDeadline}) - so the backend had
 *   by definition finished computing and the fault is in delivery.
 *
 * The distinction is the whole diagnostic value (#617, #843): a stalled backend and a stalled
 * transfer call for opposite investigations, and every abort reaches a reporter as the same
 * `AbortError` unless the phase travels alongside it.
 */
export type FetchAbortPhase = "headers" | "body"

/**
 * An `Error` carrying the phase its request was aborted in.
 *
 * The phase rides on the error rather than being derived where the error is reported, because
 * the only code that can know it is the fetch that armed the two deadlines - by the time a
 * catch block several layers up sees the failure, both deadlines have been cleared.
 */
export interface AbortPhaseError extends Error {
  readonly abortPhase: FetchAbortPhase
}

/**
 * Sentry tag name, and span attribute name, for {@link FetchAbortPhase}.
 *
 * Defined once for the same reason `fetchErrorTags` above is: the tag is written by the Sentry
 * client and the span attribute by the fetch layer, and two spellings of one dimension would
 * make any query over it find half the data.
 */
export const ABORT_PHASE_TAG = "abortPhase"

/**
 * Records the phase on an error, returning that same error so it can be thrown directly.
 *
 * Mutation is the only option available: the value has to stay a real `Error` instance for
 * `instanceof` checks and for Sentry's stack extraction, so the property cannot be supplied at
 * construction without subclassing - and subclassing changes `error.name`, which is half of
 * Sentry's grouping key. `createHeaderPhaseTimeoutError` in initialScrs.ts is where that
 * matters.
 */
export const withAbortPhase = <E extends Error>(
  error: E,
  phase: FetchAbortPhase
): E & AbortPhaseError => Object.assign(error, { abortPhase: phase })

/**
 * Reads the abort phase off an error, or null when it carries none - which is most errors,
 * since only a timeout from an instrumented fetch sets one.
 *
 * The property is validated against the two legal phases rather than trusted: anything at all
 * reaches a Sentry `beforeSend` hook, including errors this codebase did not construct, and a
 * tag whose value is some arbitrary object is worse than no tag.
 */
export const abortPhaseOf = (error: unknown): FetchAbortPhase | null => {
  if (!(error instanceof Error)) {
    return null
  }
  const phase: unknown = (error as { readonly abortPhase?: unknown }).abortPhase
  if (phase === "headers" || phase === "body") {
    return phase
  }
  return null
}

/**
 * Which of the two body-phase deadlines fired (#1554).
 *
 * - `"idle"` - no byte of the body arrived for the idle deadline: the bytes stopped.
 * - `"total"` - bytes were still arriving when the total cap, measured from headers, ran out:
 *   the delivery was too slow rather than stalled.
 *
 * These call for different remedies - compression (#484) helps a slow download and does
 * nothing for stopped bytes - which is why a body-phase abort says which one it was.
 */
export type BodyDeadline = "idle" | "total"

/**
 * What a body-phase read had seen when one of its deadlines fired.
 */
export interface BodyDeadlineDetails {
  readonly deadline: BodyDeadline
  /**
   * Body bytes read before the deadline fired; null when the body could not be read
   * incrementally, so nothing counted them.
   */
  readonly bytesReceived: number | null
  /** Milliseconds from response headers arriving to the deadline firing. */
  readonly msSinceHeaders: number
  /** The response's `Content-Length`; null when absent or not a non-negative integer. */
  readonly expectedBytes: number | null
}

/**
 * Sentry tag name for {@link BodyDeadline}. A tag rather than an `extra` because "how many of
 * these were stalls" has to be answerable by search across events, not by opening each one.
 */
export const BODY_DEADLINE_TAG = "bodyDeadline"

/**
 * Whether a value is a byte or millisecond count as the fetch layer records them: a
 * non-negative safe integer.
 */
const isCount = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0

/**
 * Reads the body-deadline details off an error, or null when it carries none or carries them
 * malformed.
 *
 * Validated field by field for the reason `abortPhaseOf` is: anything at all reaches a Sentry
 * `beforeSend` hook, and a tag or extra holding an arbitrary object is worse than none. The
 * whole record is rejected if any field is wrong, rather than reporting the valid ones, because
 * a partial record cannot be told apart from a complete one in Sentry. A record whose error does
 * not also carry the `"body"` phase is rejected too: only a body-phase abort has a body deadline.
 */
// AIDEV-NOTE (#1554): structural, not `instanceof InitialScrsBodyPhaseTimeoutError`, for the
// reason classifyFetchFailure's note gives - initialScrs.ts imports this module, so naming its
// classes here would close an import cycle, and services/sentry.ts must be able to read this
// without importing initialScrs.ts, which imports services/sentry.
export const bodyDeadlineDetailsOf = (error: unknown): BodyDeadlineDetails | null => {
  if (abortPhaseOf(error) !== "body") {
    return null
  }
  const candidate = error as {
    readonly deadline?: unknown
    readonly bytesReceived?: unknown
    readonly msSinceHeaders?: unknown
    readonly expectedBytes?: unknown
  }
  const { deadline, bytesReceived, msSinceHeaders, expectedBytes } = candidate
  if (
    (deadline !== "idle" && deadline !== "total") ||
    !(bytesReceived === null || isCount(bytesReceived)) ||
    !isCount(msSinceHeaders) ||
    !(expectedBytes === null || isCount(expectedBytes))
  ) {
    return null
  }
  return { deadline, bytesReceived, msSinceHeaders, expectedBytes }
}

/**
 * What kind of failure a request failed with, as far as the user is concerned.
 *
 * - `"network"` - the request never completed at the network layer: no connection, DNS
 *   failure, the browser cancelling it. Nothing reached the backend.
 * - `"timeout"` - a deadline fired, in either phase. `"headers"` means nothing is known about
 *   whether the backend was reached; `"body"` means it answered and the delivery stalled or
 *   was too slow.
 *   Both leave the caller with no result and both are recovered the same way, by trying
 *   again, which is why one member covers them.
 * - `"unclassified"` - anything else, including a non-ok status, a response that failed
 *   validation, and any error this codebase did not construct.
 */
export type FetchFailureKind = "network" | "timeout" | "unclassified"

/**
 * Sorts a failure from a fetch call site into the three kinds above.
 *
 * Structural, not exhaustive: `"unclassified"` is a real answer and the default one. A caller
 * uses it to decide what to *tell the user* - the three connectivity shapes name no fault of
 * theirs and are worth a retry, whereas an unrecognised error must keep whatever the call site
 * already said about it rather than being relabelled a network fault on no evidence.
 */
// AIDEV-NOTE: (#991) Deliberately not aware of `InitialScrsCancelledError` or of any other type
// from initialScrs.ts: that module imports this one, so naming its classes here would close an
// import cycle. A caller-requested cancellation therefore lands in `"unclassified"` and keeps
// the call site's existing message - which is #643's subject, not this function's.
export const classifyFetchFailure = (error: unknown): FetchFailureKind => {
  if (isNetworkLayerError(error)) {
    return "network"
  }
  if (abortPhaseOf(error) !== null) {
    return "timeout"
  }
  return "unclassified"
}
